import React from 'react';
import { NavigationScreen } from '@/types';
import { Video, UploadCloud, Radio, Cpu, HardDrive } from 'lucide-react';
import { PwaInstallButton } from './PwaInstallButton';

interface HeaderProps {
  currentScreen: NavigationScreen;
  onNavigate: (screen: NavigationScreen) => void;
  isRecordingActive: boolean;
  onImportFile: () => void;
}

const SCREEN_TITLES: Record<NavigationScreen, { title: string; subtitle: string }> = {
  onboarding: { title: 'Welcome & Onboarding', subtitle: 'First-time setup, hardware permissions & local directories' },
  dashboard: { title: 'Studio Overview', subtitle: 'Recent recordings, participant statistics & processing status' },
  studio: { title: 'Recording Studio', subtitle: 'Configure screen capture sources, system audio and video settings' },
  'podcast-studio': { title: 'Podcast Studio Pro', subtitle: 'Studio-grade local podcast recording with external mic & camera controls' },
  'group-meeting': { title: 'Group Meeting Room', subtitle: 'Multi-participant online video conference & screen presentation' },
  scheduler: { title: 'Podcast & Meeting Scheduler', subtitle: 'Plan studio recordings, sync with Google Calendar & invite participants' },
  'active-recording': { title: 'Live Capture in Progress', subtitle: 'Recording active screen feed and audio channels' },
  processing: { title: 'Processing Studio', subtitle: 'Local computer-vision tile separation and audio alignment' },
  'separate-videos': { title: 'Separate Videos', subtitle: 'Host & Guest participant separation: Adjust bounding crops & split into separate videos' },
  'participant-review': { title: 'Video Editor Studio', subtitle: 'Clipchamp-style timeline editor: Split clips (S), detach audio, add frames & export' },
  'recording-details': { title: 'Recording Details', subtitle: 'Inspect master video playback, clips, and technical metadata' },
  'export-center': { title: 'Export Center', subtitle: 'Generate individual participant MP4s, ZIP packages, and subtitles' },
  library: { title: 'Recording Library', subtitle: 'Manage all historical meeting sessions and participant assets' },
  settings: { title: 'Application Settings', subtitle: 'Storage folders, local CV models, audio inputs and diagnostics' },
};

export const Header: React.FC<HeaderProps> = ({
  currentScreen,
  onNavigate,
  isRecordingActive,
  onImportFile
}) => {
  const currentMeta = SCREEN_TITLES[currentScreen] || { title: 'MeetSplit AI', subtitle: 'Record Once. Split Every Speaker.' };

  return (
    <header className="h-16 px-6 bg-brand-surface/40 backdrop-blur-md border-b border-brand-border/60 flex items-center justify-between shrink-0 select-none">
      {/* Title & Subtitle */}
      <div>
        <h2 className="text-base font-bold text-brand-text flex items-center gap-2">
          {currentMeta.title}
          {isRecordingActive && currentScreen !== 'active-recording' && (
            <span
              onClick={() => onNavigate('active-recording')}
              className="cursor-pointer flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-semibold rounded-full bg-brand-recording text-white animate-pulse"
            >
              <Radio className="w-3 h-3 animate-spin" />
              Recording Active
            </span>
          )}
        </h2>
        <p className="text-xs text-brand-muted hidden sm:block">{currentMeta.subtitle}</p>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-3">
        {/* PWA Install App (Google Meet style) */}
        <PwaInstallButton variant="compact" />

        {/* Import Video Button */}
        <button
          onClick={onImportFile}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-brand-elevated hover:bg-brand-elevated/80 text-brand-muted hover:text-brand-text border border-brand-border transition-colors"
          title="Import an existing meeting MP4 to split participants offline"
        >
          <UploadCloud className="w-3.5 h-3.5 text-brand-secondary" />
          <span className="hidden md:inline">Import MP4</span>
        </button>

        {/* Start Recording Button */}
        {!isRecordingActive ? (
          <button
            onClick={() => onNavigate('studio')}
            className="flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-xl bg-brand-accent hover:bg-brand-accent-hover text-white shadow-glow transition-all active:scale-95"
          >
            <Video className="w-4 h-4" />
            <span>New Recording</span>
          </button>
        ) : (
          <button
            onClick={() => onNavigate('active-recording')}
            className="flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-xl bg-brand-recording hover:bg-brand-recording/90 text-white shadow-recording-glow animate-pulse transition-all"
          >
            <Radio className="w-4 h-4" />
            <span>Active Session</span>
          </button>
        )}
      </div>
    </header>
  );
};
